import { useCallback } from 'react';
import { TimeSignal, useUpdateTimeSignalMutation } from 'graphql/types';
import { updateCache } from '../../addNote/hooks';
import { useTimeSignalGetParam } from '../../hooks/useTimeSignalParam';
import {
  NOTES_PAGE_TYPE,
  useNotesFilterContext
} from '../../NotesFilterContextProvider';
import { TIMELINE_TIMESIGNAL_COUNT } from '../../Timeline';
import { useNotesSortContext } from '../../NotesSortContextProvider';
import { useNotesToastContext } from '../../NotesToastContextProvider';
import { truncateMessage } from '../../common';

export type HandleTimeSignalExcludeActionProps = {
  note: TimeSignal;
  excluded: boolean;
};
export type HandleTimeSignalExcludeActionCallback = (
  props: HandleTimeSignalExcludeActionProps
) => Promise<void>;

type ReturnType = {
  handleTimeSignalExcludeAction: HandleTimeSignalExcludeActionCallback;
  isActionInProgress: boolean;
};

const getExcludedSuccessMessage = (excluded: boolean) => {
  return excluded
    ? 'toast.note.excluded.success'
    : 'toast.note.included.success';
};

export const useTimeSignalExcludeAction = (): ReturnType => {
  const {
    filterContext: { selectedClassification, searchCriteria, pageType }
  } = useNotesFilterContext();
  const { sortOrder } = useNotesSortContext();
  const { setToastContext } = useNotesToastContext();
  const [updateNote, { loading, error }] = useUpdateTimeSignalMutation();
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );

  const handleTimeSignalExcludeAction = useCallback(
    async ({ note, excluded }: HandleTimeSignalExcludeActionProps) => {
      try {
        await updateNote({
          variables: {
            input: {
              id: note.id as string,
              excluded: excluded
            }
          },
          update: updateCache({
            param,
            selectedClassification,
            searchCriteria,
            sortOrder
          }),
          awaitRefetchQueries: false,
          refetchQueries: ['timeSignalsTotals']
        });
      } catch (e) {
        setToastContext({
          showError: true,
          message: 'toast.note.failure'
        });
      }

      if (!loading && !error) {
        setToastContext({
          showSuccess: true,
          message: getExcludedSuccessMessage(excluded),
          values: {
            message: truncateMessage(note?.description ?? '')
          }
        });
      }
    },
    [
      error,
      loading,
      param,
      searchCriteria,
      selectedClassification,
      setToastContext,
      sortOrder,
      updateNote
    ]
  );
  return {
    handleTimeSignalExcludeAction: handleTimeSignalExcludeAction,
    isActionInProgress: loading
  };
};
